/**
 * ConfirmDialog — herbruikbaar in-app bevestigings-component met optioneel bevestigingswoord.
 *
 * Gebruik via de useConfirm hook (zie onder):
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: '...', message: '...', confirmWord: 'VERWIJDER' });
 *   if (!ok) return;
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Warning, CheckCircle, X } from '@phosphor-icons/react';

const ConfirmContext = createContext(null);

const TONE_STYLES = {
  danger: {
    headerBg: 'bg-red-50 border-red-200',
    headerText: 'text-red-900',
    iconColor: 'text-red-600',
    confirmBtn: 'bg-red-600 hover:bg-red-700',
    border: 'border-red-200',
    inputFocus: 'border-red-400',
    inputValid: 'border-red-500 bg-red-50 text-red-700',
    chip: 'bg-red-100 text-red-700',
  },
  warning: {
    headerBg: 'bg-amber-50 border-amber-200',
    headerText: 'text-amber-900',
    iconColor: 'text-amber-600',
    confirmBtn: 'bg-amber-600 hover:bg-amber-700',
    border: 'border-amber-200',
    inputFocus: 'border-amber-400',
    inputValid: 'border-amber-500 bg-amber-50 text-amber-700',
    chip: 'bg-amber-100 text-amber-700',
  },
  info: {
    headerBg: 'bg-blue-50 border-blue-200',
    headerText: 'text-blue-900',
    iconColor: 'text-[#002FA7]',
    confirmBtn: 'bg-[#002FA7] hover:bg-[#1E40AF]',
    border: 'border-blue-200',
    inputFocus: 'border-blue-400',
    inputValid: 'border-blue-500 bg-blue-50 text-blue-700',
    chip: 'bg-blue-100 text-blue-700',
  },
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const [resolver, setResolver] = useState(null);
  const [input, setInput] = useState('');

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setOpts({
        title: 'Bevestigen',
        message: '',
        tone: 'danger',
        confirmText: 'Bevestigen',
        cancelText: 'Annuleren',
        confirmWord: null,        // bv. 'VERWIJDER' — gebruiker moet dit exact typen
        details: null,            // optioneel: extra info-blok onderaan boodschap
        ...options,
      });
      setInput('');
      setResolver(() => resolve);
    });
  }, []);

  const close = (result) => {
    if (resolver) resolver(result);
    setOpts(null);
    setResolver(null);
    setInput('');
  };

  // ESC sluit de dialoog
  useEffect(() => {
    if (!opts) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  const tone = opts ? (TONE_STYLES[opts.tone] || TONE_STYLES.danger) : TONE_STYLES.danger;
  const wordOk = !opts?.confirmWord || input === opts.confirmWord;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => close(false)}
          data-testid="confirm-dialog"
        >
          <div
            className={`bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden border-2 ${tone.border}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${tone.headerBg} border-b-2 px-5 py-4 flex items-center gap-3 ${tone.border}`}>
              {opts.tone === 'info'
                ? <CheckCircle size={28} weight="fill" className={`${tone.iconColor} shrink-0`} />
                : <Warning size={28} weight="fill" className={`${tone.iconColor} shrink-0`} />}
              <div className="flex-1">
                <div className={`text-lg font-bold ${tone.headerText}`}>{opts.title}</div>
                {opts.subtitle && (
                  <div className={`text-xs ${tone.headerText} opacity-80`}>{opts.subtitle}</div>
                )}
              </div>
              <button
                onClick={() => close(false)}
                className={`p-1 rounded hover:bg-white/40 ${tone.headerText}`}
                title="Sluiten"
                data-testid="confirm-dialog-x"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-gray-700">
              {typeof opts.message === 'string'
                ? <p className="whitespace-pre-wrap leading-relaxed">{opts.message}</p>
                : opts.message}
              {opts.details && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600">
                  {opts.details}
                </div>
              )}
              {opts.confirmWord && (
                <div className="pt-1">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Typ <span className={`font-mono px-1.5 py-0.5 rounded ${tone.chip}`}>{opts.confirmWord}</span> om te bevestigen
                  </label>
                  <input
                    type="text"
                    autoFocus
                    value={input}
                    onChange={(e) => setInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && wordOk) close(true);
                    }}
                    placeholder={opts.confirmWord}
                    className={`w-full px-3 py-2 text-base font-mono border-2 rounded focus:outline-none ${
                      wordOk && input ? tone.inputValid : `border-gray-300 focus:${tone.inputFocus}`
                    }`}
                    data-testid="confirm-dialog-input"
                  />
                </div>
              )}
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={() => close(false)}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                data-testid="confirm-dialog-cancel"
              >
                {opts.cancelText}
              </button>
              <button
                onClick={() => close(true)}
                disabled={!wordOk}
                className={`px-4 py-2 text-sm font-semibold text-white rounded inline-flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed ${tone.confirmBtn}`}
                data-testid="confirm-dialog-ok"
                autoFocus={!opts.confirmWord}
              >
                {opts.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Veilige fallback wanneer provider ontbreekt: terug naar browser-confirm
    return ({ title, message }) => Promise.resolve(window.confirm(`${title}\n\n${message}`));
  }
  return ctx;
}

export default ConfirmProvider;
